/**
 * @fileoverview The arithmetic Amorty is built on.
 * @module lib/domain/amortisation
 *
 * @description
 * Pure functions over plain data: no storage, no React, no I/O. That is what
 * makes them trivially testable, and what will let them run unchanged the day
 * the data comes from Postgres instead of the browser.
 *
 * Money is handled in cents throughout. Floating-point euros silently drift
 * (0.1 + 0.2 !== 0.3), and a tool about the real cost of things cannot drift.
 */

import type {
	AdvancedPortfolioStats,
	Amortisation,
	PortfolioStats,
	Purchase,
} from "@/types/types"

/** Category label used when a purchase was not assigned one. */
export const UNCATEGORISED = "Sans catégorie"

/**
 * Computes the amortisation state of a purchase.
 *
 * @param purchase - The purchase to evaluate.
 * @returns Its cost per use, progress ratio and remaining uses.
 *
 * @example
 * // A 80 € polo worn 40 times out of a 80-use target
 * computeAmortisation(polo)
 * // => { costPerUseCents: 200, targetCostPerUseCents: 100, ratio: 0.5, ... }
 */
export function computeAmortisation(purchase: Purchase): Amortisation {
	const { priceCents, targetUses, currentUses } = purchase

	// An unused item has no cost per use yet: dividing by zero would say
	// "infinitely expensive", which is true but useless to display.
	const costPerUseCents =
		currentUses > 0 ? Math.round(priceCents / currentUses) : null

	// A zero target would also divide by zero; treat it as "one use is enough".
	const safeTarget = targetUses > 0 ? targetUses : 1

	const ratio = Math.min(1, Math.max(0, currentUses / safeTarget))

	return {
		costPerUseCents,
		targetCostPerUseCents: Math.round(priceCents / safeTarget),
		ratio,
		isAmortised: currentUses >= safeTarget,
		remainingUses: Math.max(0, safeTarget - currentUses),
	}
}

/**
 * Aggregates a collection of purchases.
 *
 * @param purchases - Every tracked purchase.
 * @returns Totals and averages across the collection.
 *
 * @example
 * computePortfolioStats(purchases).averageCostPerUseCents
 */
export function computePortfolioStats(
	purchases: readonly Purchase[]
): PortfolioStats {
	let totalSpentCents = 0
	let amortisedCount = 0
	let amortisedSpentCents = 0
	let usedSpentCents = 0
	let totalUses = 0

	for (const purchase of purchases) {
		totalSpentCents += purchase.priceCents

		if (computeAmortisation(purchase).isAmortised) {
			amortisedCount += 1
			amortisedSpentCents += purchase.priceCents
		}

		if (purchase.currentUses > 0) {
			usedSpentCents += purchase.priceCents
			totalUses += purchase.currentUses
		}
	}

	return {
		purchaseCount: purchases.length,
		totalSpentCents,
		amortisedCount,
		amortisedSpentCents,
		// Weighted by spend, not a mean of means: 1 000 € used twice must not
		// be averaged away by a 5 € item used a hundred times.
		averageCostPerUseCents:
			totalUses > 0 ? Math.round(usedSpentCents / totalUses) : null,
	}
}

/**
 * Aggregates the deeper figures shown once the statistics panel expands.
 *
 * @param purchases - Every tracked purchase.
 * @returns Usage totals, the best return so far, and spend per category.
 *
 * @example
 * computeAdvancedStats(purchases).categoryBreakdown
 */
export function computeAdvancedStats(
	purchases: readonly Purchase[]
): AdvancedPortfolioStats {
	let totalUses = 0
	let unusedCount = 0
	let ratioSum = 0
	let bestValue: {
		id: string
		label: string
		costPerUseCents: number
	} | null = null

	const byCategory = new Map<
		string,
		{ spentCents: number; purchaseCount: number }
	>()

	for (const purchase of purchases) {
		totalUses += purchase.currentUses
		if (purchase.currentUses === 0) unusedCount += 1

		const amortisation = computeAmortisation(purchase)
		ratioSum += amortisation.ratio

		if (
			amortisation.costPerUseCents !== null &&
			(bestValue === null ||
				amortisation.costPerUseCents < bestValue.costPerUseCents)
		) {
			bestValue = {
				id: purchase.id,
				label: purchase.label,
				costPerUseCents: amortisation.costPerUseCents,
			}
		}

		const category = purchase.category ?? UNCATEGORISED
		const bucket = byCategory.get(category) ?? {
			spentCents: 0,
			purchaseCount: 0,
		}
		byCategory.set(category, {
			spentCents: bucket.spentCents + purchase.priceCents,
			purchaseCount: bucket.purchaseCount + 1,
		})
	}

	const categoryBreakdown = [...byCategory.entries()]
		.map(([category, bucket]) => ({ category, ...bucket }))
		.sort((a, b) => b.spentCents - a.spentCents)

	return {
		totalUses,
		unusedCount,
		averageRatio: purchases.length > 0 ? ratioSum / purchases.length : 0,
		bestValue,
		categoryBreakdown,
	}
}

/**
 * Formats a cent amount as euros.
 *
 * @param cents - Amount in cents, or null.
 * @param fallback - Text returned when the amount is null.
 * @returns A localised string such as `1,27 €`.
 */
export function formatCents(cents: number | null, fallback = "—"): string {
	if (cents === null) return fallback

	return new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency: "EUR",
	}).format(cents / 100)
}

/**
 * Formats a ratio as a whole percentage.
 *
 * @param ratio - Value between 0 and 1.
 * @returns A string such as `46 %`.
 */
export function formatRatio(ratio: number): string {
	return `${String(Math.round(ratio * 100))} %`
}
